/**
 * Chart Block - Interactivity API View Script
 *
 * The chart is fully drawn by the server and the tooltips are CSS, so the only
 * job here is the reveal: add a class when the chart scrolls into view and let
 * the stylesheet's keyframes play once.
 *
 * The animation is deliberately powerless over the values. The final geometry is
 * what the server rendered and what the stylesheet describes; the keyframes have
 * no fill mode, so if they never run — reduced motion, a background tab where
 * the browser refuses to tick animations, no JavaScript at all — the marks are
 * already at their true size. A chart is never left showing an empty plot
 * because an animation was queued and never played.
 *
 * @package BrandyBlocks
 */

import { store, getElement } from '@wordpress/interactivity';

const REVEAL_CLASS = 'is-revealing';

store( 'brandy/chart', {
	callbacks: {
		reveal() {
			const { ref } = getElement();

			if (
				window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
			) {
				return;
			}

			const observer = new IntersectionObserver(
				( entries ) => {
					if ( ! entries[ 0 ].isIntersecting ) {
						return;
					}

					observer.disconnect();

					// A hidden tab does not tick animations. Starting one there
					// would burn the single reveal on a frame nobody sees, so
					// the chart just stays at its finished state.
					if ( document.visibilityState !== 'visible' ) {
						return;
					}

					// The class stays. Removing it on `animationend` would fire
					// on the first mark to finish and snap the staggered rest
					// to their end state mid-flight; leaving it is harmless
					// because the keyframes run once and hold no fill.
					ref.classList.add( REVEAL_CLASS );
				},
				{ threshold: 0.15 }
			);

			observer.observe( ref );

			return () => observer.disconnect();
		},
	},
} );
